import { createAsyncThunk } from '@reduxjs/toolkit';
import fileDownload from 'js-file-download';

import { ApplicantProps, BusinessDocumentTypes } from 'entities/businessInfo';
import { hasPermission, getCountries } from 'pages/dashboard/selectors';
import * as businessDocsService from 'services/businessDocuments';
import { setBusinessInfo } from 'pages/Profile/profileSlice';
import { updatePersonTelephone } from 'pages/Profile/thunks';
import { updateBusinesses } from 'pages/Profile/api';
import { updateAddresses } from 'services/addresses';
import { getProfile } from 'pages/Profile/selectors';
import { Permissions } from 'entities/dashboard';
import { isValidPhoneNumber } from 'utils/phone';
import { getUploadOptions } from 'utils/files';
import { getPerson } from 'services/persons';
import * as ERPService from 'services/erp';
import { buildFormData, objectMap } from 'utils/helpers';
import { RootState } from 'state/store';
import {
  getBusinessDocumentTypes,
  getBusinessLegalEntities,
} from 'services/countries';
import telephoneService, {
  BUSINESS_PHONE_TYPE_CODE,
} from 'services/telephoneNumbers';
import {
  getBusinessSourceTypes,
  searchBusinessesPersonsXrefs,
} from 'services/businesses';

import { getRoot } from './selectors';
import {
  setLoadingProgress,
  setERPPreferences,
  setApplicant,
} from './businessInfoSlice';
import {
  mapApplicantData,
  getUpdateBusinessFields,
  getUpdateBusinessAddressFields,
  getUpdateBusinessPhoneFields,
  getAddDocumentFields,
  getUpdateBusinessERPPreferences,
  calcBusinessInfoPercentage,
  getMappedBusinessInfo,
  validateIdNumber,
} from './utils';

export const checkBusinessInfoProgress: any = createAsyncThunk(
  'financial/checkBusinessInfoProgress',
  async (data: any) => {
    try {
      return calcBusinessInfoPercentage(data);
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const fetchBusinessDocumentTypes: any = createAsyncThunk(
  'businessInfo/fetchBusinessDocumentTypes',
  async (countryIsocode: string, thunkAPI) => {
    const countries = getCountries(thunkAPI.getState() as RootState);

    const countryId = countries.find(
      (c) => c.iSOCode === countryIsocode,
    )?.countriesId;

    if (countryId) {
      const types = await getBusinessDocumentTypes(countryId);

      return {
        idTypes: types,
        idTypesCountryId: countryIsocode,
      };
    }

    return {
      idTypes: [],
      idTypesCountryId: countryIsocode,
    };
  },
);

export const fetchBusinessLegalEntities: any = createAsyncThunk(
  'businessInfo/fetchBusinessLegalEntities',
  async (countryIsocode: string, thunkAPI) => {
    const countries = getCountries(thunkAPI.getState() as RootState);

    const countryId = countries.find(
      (c) => c.iSOCode === countryIsocode,
    )?.countriesId;

    if (countryId) {
      const types = await getBusinessLegalEntities(countryId);

      return {
        legalEntityTypes: types,
        idTypesCountryId: countryIsocode,
      };
    }

    return {
      legalEntityTypes: [],
      idTypesCountryId: countryIsocode,
    };
  },
);

export const fetchBusinessAdditionalData: any = createAsyncThunk(
  'businessInfo/fetchBusinessAdditionalData',
  async (data: any, thunkAPI) => {
    try {
      const { business, person, personTelephoneNumber, businessPerson } = data;

      const state = thunkAPI.getState() as RootState;

      const { view: viewSourcePermission } = hasPermission(
        state,
        Permissions.erpIntegration,
      );

      let sourceTypes: any = [];
      if (viewSourcePermission) {
        sourceTypes = await getBusinessSourceTypes(business.businessesId);
      }

      const { view: viewBusinessPermission } = hasPermission(
        state,
        Permissions.onboardingBusiness,
      );

      if (!viewBusinessPermission) {
        return { sourceTypes };
      }

      const documents = await businessDocsService.searchBusinessesDocuments({
        businessesId: business.businessesId,
        isActive: true,
      });

      const erpPreferences = await ERPService.searchERPPreferencesData({
        businessesId: business.businessesId,
        isActive: true,
      });

      let applicant: any = {
        givenName1: person.givenName1,
        surnameFirst: person.surnameFirst,
        emailAddress: person.emailAddress,
        personsId: person.personsId,
        personCountryCallingCode: personTelephoneNumber.countryCallingCode,
        personTelephoneNumber: personTelephoneNumber.telephoneNumber,
        businessesPersonsRole: businessPerson.businessesPersonsRole,
      };

      if (person.emailAddress !== business.createdBy) {
        try {
          const realApplicant = await getPerson(business.createdBy);

          if (realApplicant) {
            const applicantBusinessPersons: any =
              await searchBusinessesPersonsXrefs({
                isActive: true,
                businessesId: business.businessesId,
                personsId: realApplicant.id,
              });

            applicant = {
              givenName1: realApplicant.givenName1,
              surnameFirst: realApplicant.surnameFirst,
              emailAddress: realApplicant.emailAddress,
              personsId: realApplicant.id,
              personCountryCallingCode: realApplicant.phone.countryCallingCode,
              personTelephoneNumber: realApplicant.phone.number,
              businessesPersonsRole: applicantBusinessPersons.length
                ? applicantBusinessPersons[0].businessesPersonsRole
                : '',
            };
          }
        } catch {}
      }

      const result = {
        sourceTypes: sourceTypes,
        documents,
        applicant: applicant ? mapApplicantData(applicant) : null,
        erpPreferences: erpPreferences.length ? erpPreferences[0] : null,
      };

      const businessInfo = getMappedBusinessInfo(result, data);

      thunkAPI.dispatch(checkBusinessInfoProgress(businessInfo));

      return result;
    } catch (e: any) {
      return Promise.reject(e);
    }
  },
);

export const uploadBusinessDocument: any = createAsyncThunk(
  'businessInfo/uploadBusinessDocument',
  async (data: any, thunkApi) => {
    const { getState, dispatch } = thunkApi;
    try {
      const state = getState() as RootState;

      const { business, person } = getProfile(state);
      const { documents } = getRoot(state);

      dispatch(setLoadingProgress(1));

      const docs = documents.slice();

      const fields = getAddDocumentFields({
        person,
        business,
        file: data.files[0],
        businessesDocumentsTypesId: data.businessesDocumentsTypesId,
      });

      const options = getUploadOptions((value) =>
        dispatch(setLoadingProgress(value)),
      );

      const docId = await businessDocsService.addBusinessesDocuments(
        fields,
        options,
      );

      if (docId) {
        dispatch(setLoadingProgress(100));

        setTimeout(() => {
          dispatch(setLoadingProgress(0));
        }, 2000);

        const document = await businessDocsService.getBusinessesDocuments(
          docId,
        );

        if (document) {
          docs.push(document);
        }
      }

      return docs;
    } catch (e) {
      dispatch(setLoadingProgress(0));
      return Promise.reject(e);
    }
  },
);

export const deleteBusinessDocument: any = createAsyncThunk(
  'businessInfo/deleteBusinessDocument',
  async (id: number, thunkApi) => {
    try {
      const state = thunkApi.getState() as RootState;

      const { person } = getProfile(state);
      const { documents } = getRoot(state);

      const docId = await businessDocsService.deleteBusinessesDocuments(
        id,
        person.emailAddress,
      );

      if (docId) {
        return documents
          .slice()
          .filter((doc) => doc.businessesDocumentsId !== id);
      }

      return documents.slice();
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const downloadBusinessDocument: any = createAsyncThunk(
  'businessInfo/downloadBusinessDocument',
  async ({ guid, fileName }: any) => {
    try {
      const file = await businessDocsService.getBusinessesDocumentsFile(guid);

      fileDownload(file, fileName);
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const updateBusinessDocument: any = createAsyncThunk(
  'businessInfo/updateBusinessDocument',
  async (data: any, thunkApi) => {
    try {
      const state = thunkApi.getState() as RootState;

      const { business, person } = getProfile(state);
      const { documents } = getRoot(state);

      const { idnumber, businessesDocumentsTypesId } = data;
      const businessDocumentTypeId = businessesDocumentsTypesId || '';

      const idType = state.businessInfo.idTypes.find(
        (type) =>
          type.businessesDocumentsTypesId === businessesDocumentsTypesId,
      );

      // Re-validate the ID number against its type's rules before persisting,
      // so an invalid value never reaches the backend.
      if (validateIdNumber(idType?.typeCode ?? '', idnumber)) {
        return documents;
      }

      const docs = documents.slice();
      const documentIndex: any = docs.findIndex(
        (doc) =>
          doc.businessesDocumentsTypesId !==
            BusinessDocumentTypes.centralIndexKey &&
          doc.businessesDocumentsTypesId !== BusinessDocumentTypes.dbaStatement,
      );

      if (documentIndex >= 0) {
        if (!idnumber && !businessesDocumentsTypesId) {
          await businessDocsService.deleteBusinessesDocuments(
            docs[documentIndex].businessesDocumentsId,
            person.emailAddress,
          );

          return docs.filter(
            (doc) =>
              doc.businessesDocumentsId !==
              docs[documentIndex].businessesDocumentsId,
          );
        }

        if (
          docs[documentIndex].idnumber !== idnumber ||
          docs[documentIndex].businessesDocumentsTypesId !==
            businessDocumentTypeId
        ) {
          const payload = { ...docs[documentIndex] };
          payload.modifiedBy = person.emailAddress;
          payload.idnumber = idnumber;
          payload.businessesDocumentsTypesId = businessDocumentTypeId;

          const formData = buildFormData(payload);

          await businessDocsService.updateBusinessesDocuments(formData);

          docs[documentIndex] = {
            ...docs[documentIndex],
            idnumber,
            businessesDocumentsTypesId: businessDocumentTypeId,
          };
        }
      } else if (idnumber || businessesDocumentsTypesId) {
        const formData = new FormData();
        formData.append('businessesDocumentsTypesId', businessDocumentTypeId);
        formData.append('idnumber', idnumber);
        formData.append('isActive', 'true');
        formData.append('createdBy', person.emailAddress);
        formData.append('businessesId', business.businessesId.toString());

        const businessDocId = await businessDocsService.addBusinessesDocuments(
          formData,
        );
        const document: any = await businessDocsService.getBusinessesDocuments(
          businessDocId,
        );

        docs.push(document);
      }

      return docs;
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const updateCentralIndexDocument: any = createAsyncThunk(
  'businessInfo/updateCentralIndexDocument',
  async (data: any, thunkApi) => {
    try {
      const state = thunkApi.getState() as RootState;

      const { business, person } = getProfile(state);
      const { documents } = getRoot(state);

      const { idnumber, businessesDocumentsTypesId } = data;
      const businessDocumentTypeId = businessesDocumentsTypesId || '';

      const docs = documents.slice();
      const documentIndex: any = docs.findIndex(
        (doc) => doc.businessesDocumentsTypesId === businessesDocumentsTypesId,
      );

      if (documentIndex >= 0) {
        if (!idnumber) {
          await businessDocsService.deleteBusinessesDocuments(
            docs[documentIndex].businessesDocumentsId,
            person.emailAddress,
          );
          return docs.filter(
            (doc) =>
              doc.businessesDocumentsId !==
              docs[documentIndex].businessesDocumentsId,
          );
        }

        if (docs[documentIndex].idnumber !== idnumber) {
          const payload = { ...docs[documentIndex] };
          payload.modifiedBy = person.emailAddress;
          payload.idnumber = idnumber;
          payload.businessesDocumentsTypesId = businessDocumentTypeId;

          const formData = buildFormData(payload);

          await businessDocsService.updateBusinessesDocuments(formData);

          docs[documentIndex] = {
            ...docs[documentIndex],
            idnumber,
          };
        }
      } else if (idnumber) {
        const formData = new FormData();
        formData.append('idnumber', idnumber);
        formData.append('businessesDocumentsTypesId', businessDocumentTypeId);
        formData.append('isActive', 'true');
        formData.append('createdBy', person.emailAddress);
        formData.append('businessesId', business.businessesId.toString());
        formData.append(
          'businessesDocumentsTypesId',
          businessesDocumentsTypesId.toString(),
        );

        const federalDocId = await businessDocsService.addBusinessesDocuments(
          formData,
        );
        const document: any = await businessDocsService.getBusinessesDocuments(
          federalDocId,
        );

        docs.push(document);
      }

      return docs;
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

const handleApplicantPhone = async (
  data: any,
  applicant: ApplicantProps,
  dispatch: any,
) => {
  if (data.applicantPhone && data.applicantPhone !== applicant.applicantPhone) {
    const isValidPhone = isValidPhoneNumber(
      data.applicantPhone,
      data.alpha2Code,
    );

    if (!isValidPhone) {
      return;
    }

    try {
      await dispatch(
        updatePersonTelephone({
          phone: data.applicantPhone,
          personsId: applicant.personsId,
        }),
      ).unwrap();

      dispatch(
        setApplicant({
          ...applicant,
          applicantPhone: data.applicantPhone,
        }),
      );
    } catch {}
  }
};

export const updateBusinessInfo: any = createAsyncThunk(
  'businessInfo/updateBusinessInfo',
  async (data: any, thunkApi) => {
    try {
      const { dispatch, getState } = thunkApi;

      const state = getState() as RootState;

      const { showDoingBusinessAs, erpPreferences } = getRoot(state);
      const {
        business,
        person,
        businessAddress,
        businessTelephoneNumber,
        businessTelephoneNumberXref,
        onboarding,
      } = getProfile(state);

      let isFormUpdate = true;

      if (!business.businessesId) {
        return isFormUpdate;
      }

      handleApplicantPhone(data, state.businessInfo.applicant, dispatch);

      const updateBusinessFields = getUpdateBusinessFields({
        data,
        showDoingBusinessAs,
        business,
      });

      if (updateBusinessFields) {
        isFormUpdate = false;
        await updateBusinesses({
          logoFileShareURI: business.logoFileShareURI,
          faviconFileShareURI: business.faviconFileShareURI,
          businessesId: business.businessesId,
          modifiedBy: person.emailAddress,
          ...updateBusinessFields,
        });
      }

      const updateBusinessAddressFields = getUpdateBusinessAddressFields({
        data,
        businessAddress,
      });

      if (updateBusinessAddressFields) {
        isFormUpdate = false;
        await updateAddresses({
          addressesId: businessAddress.addressesId,
          modifiedBy: person.emailAddress,
          ...updateBusinessAddressFields,
        });
      }

      const updateBusinessPhoneFields: any = getUpdateBusinessPhoneFields({
        data,
        businessTelephoneNumber,
      });

      let businessesTelephoneNumbersXrefId: any =
        businessTelephoneNumberXref.businessesTelephoneNumbersXrefId || 0;

      if (updateBusinessPhoneFields) {
        isFormUpdate = false;

        const telephoneNumbersId = await telephoneService.addTelephoneNumbers({
          createdBy: person.emailAddress,
          ...updateBusinessPhoneFields,
        });

        updateBusinessPhoneFields.telephoneNumbersId = telephoneNumbersId;

        if (businessTelephoneNumberXref.businessesTelephoneNumbersXrefId) {
          businessesTelephoneNumbersXrefId =
            await telephoneService.updateBusinessesTelephoneNumbersXrefs({
              businessesTelephoneNumbersXrefid:
                businessTelephoneNumberXref.businessesTelephoneNumbersXrefId,
              telephoneNumbersId,
              modifiedBy: person.emailAddress,
            });
        } else {
          businessesTelephoneNumbersXrefId =
            await telephoneService.addBusinessesTelephoneNumbersXrefs({
              createdBy: person.emailAddress,
              telephoneNumbersTypeCode: BUSINESS_PHONE_TYPE_CODE,
              businessesId: business.businessesId,
              telephoneNumbersId,
              isActive: true,
            });
        }
      }

      const updateErpPreferences = getUpdateBusinessERPPreferences({
        data,
        erpPreferences,
      });

      if (updateErpPreferences && !onboarding) {
        isFormUpdate = false;

        if (erpPreferences) {
          const erpData = {
            erppreferenceDataId: erpPreferences.erppreferenceDataId,
            modifiedBy: person.emailAddress,
            ...objectMap(updateErpPreferences, (value) =>
              value === '' ? ' ' : value,
            ),
          };

          await ERPService.updateERPPreferencesData(erpData);

          dispatch(
            setERPPreferences({
              ...erpPreferences,
              ...updateErpPreferences,
            }),
          );
        } else {
          const erpData = {
            isActive: true,
            businessesId: business.businessesId,
            createdBy: person.emailAddress,
            requiresDigitalSignature: true,
            ...updateErpPreferences,
          };

          await ERPService.addERPPreferencesData(erpData);

          dispatch(setERPPreferences(erpData));
        }
      }

      dispatch(
        setBusinessInfo({
          business: updateBusinessFields,
          businessAddress: {
            ...businessAddress,
            ...updateBusinessAddressFields,
          },
          businessTelephoneNumber: {
            ...businessTelephoneNumber,
            ...updateBusinessPhoneFields,
          },
          businessTelephoneNumberXref: {
            businessesTelephoneNumbersXrefId,
          },
        }),
      );

      await dispatch(
        updateBusinessDocument({
          businessesDocumentsTypesId: data.businessesDocumentsTypesId,
          idnumber: data.idNumber,
        }),
      );

      await dispatch(
        updateCentralIndexDocument({
          businessesDocumentsTypesId: BusinessDocumentTypes.centralIndexKey,
          idnumber: data.centralIndexKey,
        }),
      );

      dispatch(checkBusinessInfoProgress(data));

      return isFormUpdate;
    } catch (e) {
      return Promise.reject(e);
    }
  },
);
